import { useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, BookOpen, ChevronRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { safeStorage } from '../../utils/safeStorage';
import { logger } from '../../utils/logger';
import { todayIST } from '../../utils/locale';
import { formatMoneyMinor } from '../../utils/money';

/**
 * WeeklySummaryCard — Monday/Tuesday-only digest pulled from
 * `/api/weekly-summary`. Aggregates three quiet streams (critical
 * delegations, overdue payments, stale reading) into a single Daybreak
 * card so the user gets a single weekly-cadence nudge without having
 * to visit each view.
 *
 * Renders nothing when:
 *   - it's not Monday/Tuesday, OR
 *   - all three counts are zero, OR
 *   - the user dismissed it today (localStorage flag).
 *
 * Dismissal is purely client-side because the cadence is naturally
 * gated by `weekday in (0, 1)` on the backend.
 */
const DISMISS_KEY = 'polkdesq:weekly-summary-dismissed';

function dismissedToday() {
  if (typeof window === 'undefined') return false;
  return safeStorage.getString(DISMISS_KEY) === todayIST();
}

function markDismissedToday() {
  safeStorage.setString(DISMISS_KEY, todayIST());
}

function formatTotals(totalsMinor) {
  if (!totalsMinor || typeof totalsMinor !== 'object') return '';
  const entries = Object.entries(totalsMinor);
  if (entries.length === 0) return '';
  return entries.map(([c, m]) => formatMoneyMinor(m, c)).join(' + ');
}

export default function WeeklySummaryCard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(dismissedToday());

  useEffect(() => {
    if (dismissed) return undefined;
    let alive = true;
    const controller = new AbortController();
    (async () => {
      try {
        // Demo override: set localStorage 'polkdesq:weekly-summary-force' = '1'
        // in dev tools to see the card on any day.
        const force = safeStorage.getString('polkdesq:weekly-summary-force') === '1';
        const res = await api.get('/weekly-summary', {
          params: force ? { force: 1 } : {},
          signal: controller.signal,
        });
        if (alive) setSummary(res.data || null);
      } catch (err) {
        if (err && (err.name === 'CanceledError' || err.name === 'AbortError')) return;
        logger.warn('Weekly summary fetch failed (card hidden, non-fatal):', err?.message);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [dismissed]);

  if (dismissed || !summary || !summary.showToday) return null;

  const critical = summary.criticalDelegations || { count: 0, items: [] };
  const overdue = summary.overduePayments || { count: 0, items: [], totalsByCurrency: {} };
  const reading = summary.staleReading || { count: 0, items: [] };
  const totalSignals = summary.totalSignals || 0;

  const onDismiss = () => {
    markDismissedToday();
    setDismissed(true);
  };

  return (
    <div
      data-testid="weekly-summary-card"
      className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}
    >
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--pd-border)' }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
            Monday signal · weekly
          </div>
          <div data-testid="weekly-summary-headline" className="text-[14px] font-semibold mt-0.5" style={{ color: 'var(--pd-text)' }}>
            {totalSignals} {totalSignals === 1 ? 'thing' : 'things'} need attention this week
          </div>
        </div>
        <button
          type="button"
          data-testid="weekly-summary-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss weekly summary"
          className="p-1 rounded-lg transition-colors hover:bg-[var(--pd-panel-2)]"
        >
          <X className="w-3.5 h-3.5" style={{ color: 'var(--pd-subtle)' }} />
        </button>
      </div>
      <div className="divide-y" style={{ borderColor: 'var(--pd-border)' }}>
        {critical.count > 0 ? (
          <button
            type="button"
            data-testid="weekly-summary-delegations-row"
            onClick={() => navigate('/delegatables')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--pd-panel-2)]"
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'color-mix(in srgb, var(--pd-crimson) 14%, transparent)' }}>
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--pd-crimson)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium" style={{ color: 'var(--pd-text)' }}>
                {critical.count} critical handoff{critical.count === 1 ? '' : 's'}
              </div>
              <div className="text-[10px] truncate" style={{ color: 'var(--pd-subtle)' }}>
                {critical.items[0]?.title}{critical.items.length > 1 ? ` + ${critical.items.length - 1} more` : ''}
              </div>
            </div>
            <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--pd-subtle)' }} />
          </button>
        ) : null}
        {overdue.count > 0 ? (
          <button
            type="button"
            data-testid="weekly-summary-payments-row"
            onClick={() => navigate('/payments')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--pd-panel-2)]"
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'color-mix(in srgb, var(--pd-warning) 14%, transparent)' }}>
              <CreditCard className="w-3.5 h-3.5" style={{ color: 'var(--pd-warning)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium" style={{ color: 'var(--pd-text)' }}>
                {overdue.count} overdue payment{overdue.count === 1 ? '' : 's'} · {formatTotals(overdue.totalsByCurrencyMinor)}
              </div>
              <div className="text-[10px] truncate" style={{ color: 'var(--pd-subtle)' }}>
                {overdue.items[0]?.title} ({overdue.items[0]?.daysOverdue}d overdue){overdue.items.length > 1 ? ` + ${overdue.items.length - 1} more` : ''}
              </div>
            </div>
            <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--pd-subtle)' }} />
          </button>
        ) : null}
        {reading.count > 0 ? (
          <button
            type="button"
            data-testid="weekly-summary-reading-row"
            onClick={() => navigate('/reading')}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--pd-panel-2)]"
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'color-mix(in srgb, var(--pd-focus) 14%, transparent)' }}>
              <BookOpen className="w-3.5 h-3.5" style={{ color: 'var(--pd-focus)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium" style={{ color: 'var(--pd-text)' }}>
                {reading.count} reading item{reading.count === 1 ? '' : 's'} aging past 2 weeks
              </div>
              <div className="text-[10px] truncate" style={{ color: 'var(--pd-subtle)' }}>
                {reading.items[0]?.title} ({reading.items[0]?.ageDays}d old){reading.items.length > 1 ? ` + ${reading.items.length - 1} more` : ''}
              </div>
            </div>
            <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--pd-subtle)' }} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
